import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, ButtonBase, Stack, Typography, useTheme } from "@mui/material";
import { alpha, darken, lighten } from "@mui/material/styles";
import CardGiftcardRounded from "@mui/icons-material/CardGiftcardRounded";
import PeopleRounded from "@mui/icons-material/PeopleRounded";
import StarRounded from "@mui/icons-material/StarRounded";
import PhoneIphoneRounded from "@mui/icons-material/PhoneIphoneRounded";
import ToggleButton from "../../ToggleButton";
import NumberInput from "../../NumberInput";
import BottomButton from "../../BottomButton";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { FullScreenLoader } from "../../FullScreenLoader";
import ColoredIcon from "../../ColoredIcon";
import BackIcon from "../../../img/Arrow_Left.svg";
import { FONTS, CARD_STYLES } from "../../../constants/styles";
import Loyalty from "../../../api/Loyalty";

const NOISE_TEXTURE = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

const LoyaltyConfig: React.FC = () => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const navigate = useNavigate();

    const [cookies] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookies;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showTutorial, setShowTutorial] = useState(
        () => localStorage.getItem("loyalty_tutorial_seen") !== "true"
    );

    // Engine 1
    const [referralEnabled, setReferralEnabled] = useState(false);
    const [referralDiscount, setReferralDiscount] = useState(0);
    const [referralReward, setReferralReward] = useState(0);
    const [balanceExpiryDays, setBalanceExpiryDays] = useState(90);
    const [maxBalancePct, setMaxBalancePct] = useState(100);

    // Engine 2
    const [subscriptionEnabled, setSubscriptionEnabled] = useState(false);
    const [subPrice, setSubPrice] = useState(0);
    const [subDiscount, setSubDiscount] = useState<number | null>(null);
    const [subCashback, setSubCashback] = useState<number | null>(null);
    const [subNeverExpire, setSubNeverExpire] = useState<boolean | null>(null);
    const [subShortNotice, setSubShortNotice] = useState<number | null>(null);
    const [subShortCancel, setSubShortCancel] = useState<number | null>(null);
    const [subWaitlistPriority, setSubWaitlistPriority] = useState<boolean | null>(null);

    // App booking discount
    const [appDiscountEnabled, setAppDiscountEnabled] = useState(false);
    const [appDiscountPct, setAppDiscountPct] = useState(5);

    const scrollRef = useRef<HTMLDivElement>(null);
    const heroRef = useRef<HTMLDivElement>(null);
    const handleScroll = useCallback(() => {
        // Scroll tracking for future use
    }, []);

    useEffect(() => {
        if (!uuid || !brand_id) { navigate("/"); return; }
        Loyalty.getConfig(uuid, brand_id).then((cfg) => {
            if (!cfg) return;
            setReferralEnabled(cfg.loyaltyReferral);
            setReferralDiscount(cfg.loyaltyReferralDiscount);
            setReferralReward(cfg.loyaltyReferralReward);
            setBalanceExpiryDays(cfg.loyaltyBalanceExpiryDays);
            setMaxBalancePct(cfg.loyaltyMaxBalancePct ?? 100);
            setSubscriptionEnabled(cfg.loyaltySubscription);
            setSubPrice(cfg.loyaltySubPrice);
            setSubDiscount(cfg.loyaltySubDiscount);
            setSubCashback(cfg.loyaltySubCashback);
            setSubNeverExpire(cfg.loyaltySubNeverExpire);
            setSubShortNotice(cfg.loyaltySubShortNotice);
            setSubShortCancel(cfg.loyaltySubShortCancel);
            setSubWaitlistPriority(cfg.loyaltySubWaitlistPriority);
            setAppDiscountEnabled(cfg.loyaltyAppDiscount);
            setAppDiscountPct(cfg.loyaltyAppDiscountPct);
        }).catch(console.error).finally(() => setLoading(false));
    }, [uuid, brand_id, navigate]);

    const onSave = useCallback(async () => {
        setSaving(true);
        try {
            await Loyalty.updateConfig(uuid, brand_id, {
                loyaltyReferral: referralEnabled,
                loyaltyReferralDiscount: referralDiscount,
                loyaltyReferralReward: referralReward,
                loyaltyBalanceExpiryDays: balanceExpiryDays,
                loyaltyMaxBalancePct: maxBalancePct,
                loyaltySubscription: subscriptionEnabled,
                loyaltySubPrice: subPrice,
                loyaltySubDiscount: subDiscount,
                loyaltySubCashback: subCashback,
                loyaltySubNeverExpire: subNeverExpire,
                loyaltySubShortNotice: subShortNotice,
                loyaltySubShortCancel: subShortCancel,
                loyaltySubWaitlistPriority: subWaitlistPriority,
                loyaltyAppDiscount: appDiscountEnabled,
                loyaltyAppDiscountPct: appDiscountPct,
            });
            navigate("/");
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(false);
        }
    }, [
        uuid, brand_id, navigate,
        referralEnabled, referralDiscount, referralReward, balanceExpiryDays, maxBalancePct,
        subscriptionEnabled, subPrice, subDiscount, subCashback, subNeverExpire, subShortNotice, subShortCancel, subWaitlistPriority,
        appDiscountEnabled, appDiscountPct,
    ]);

    const dismissTutorial = () => {
        setShowTutorial(false);
        localStorage.setItem("loyalty_tutorial_seen", "true");
    };

    const iconBoxSx = {
        width: 32, height: 32, borderRadius: "10px",
        bgcolor: alpha(primary, 0.1),
        display: "flex", alignItems: "center", justifyContent: "center",
        flex: "0 0 auto",
    };

    if (loading) return <FullScreenLoader />;

    const scrolledPastHero = false; // placeholder for scroll tracking

    return (
        <>
            <Stack
                ref={scrollRef}
                onScroll={handleScroll}
                flex={1}
                sx={{ overflowY: "auto", overflowX: "hidden", bgcolor: "white" }}
            >
                {/* ── Hero (compact, matches AutomationEdit) ── */}
                {/* Hero uses symmetric `px: 2`. The back button is absolutely
                    positioned so it doesn't participate in the flex flow — the
                    icon + pill center mathematically against the container's
                    full interior width regardless of viewport size. */}
                <Stack
                    ref={heroRef}
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        width: "100%",
                        minHeight: 120,
                        position: "relative",
                        zIndex: 0,
                        background: `linear-gradient(180deg, ${lighten(primary, 0.08)} 0%, ${darken(primary, 0.08)} 100%)`,
                        pt: "40px",
                        pb: 4,
                        "&::after": {
                            content: '""', position: "absolute", inset: 0, opacity: 0.04,
                            backgroundImage: NOISE_TEXTURE, backgroundRepeat: "repeat", backgroundSize: "128px 128px", pointerEvents: "none",
                        },
                    }}
                >
                    <ButtonBase
                        onClick={() => navigate("/")}
                        sx={{
                            position: "absolute", top: 12, left: 10, zIndex: 2,
                            width: 36, height: 36, borderRadius: "50%",
                            bgcolor: "rgba(60,60,60,0.35)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                    >
                        <ColoredIcon icon={BackIcon} color="white" size="28px" />
                    </ButtonBase>

                    {/* Frosted circle + icon — mathematically centered in the
                        hero. The back arrow is absolute-positioned so it does
                        not participate in flex layout; the flex-center is the
                        true container center. */}
                    <Box
                        sx={{
                            width: 72, height: 72, borderRadius: "50%",
                            bgcolor: "rgba(60,60,60,0.3)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.15)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            mb: 1,
                        }}
                    >
                        <CardGiftcardRounded sx={{ fontSize: 40, color: "white" }} />
                    </Box>

                    {/* Frosted pill */}
                    <Box
                        sx={{
                            bgcolor: "rgba(60,60,60,0.35)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            borderRadius: "16px", px: 1.5, py: 0.5,
                        }}
                    >
                        <Typography fontSize="12px" fontWeight={500} color="white">
                            Loyalty Program
                        </Typography>
                    </Box>
                </Stack>

                {/* ── Sticky Toolbar ── */}
                <Box
                    sx={{
                        position: "sticky", top: 0, zIndex: 4, bgcolor: "white",
                        borderTopLeftRadius: "24px", borderTopRightRadius: "24px",
                        mt: "-24px", pt: 1.5,
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                            maxHeight: scrolledPastHero ? 52 : 0,
                            opacity: scrolledPastHero ? 1 : 0,
                            overflow: "hidden",
                            transition: "max-height 0.25s ease, opacity 0.2s ease",
                            px: 1,
                            borderBottom: scrolledPastHero ? "1px solid rgba(0,0,0,0.06)" : "none",
                        }}
                    >
                        <ButtonBase onClick={() => navigate("/")} sx={{ p: 0.5 }}>
                            <ColoredIcon icon={BackIcon} color={primary} size="28px" />
                        </ButtonBase>
                        <Typography fontFamily={FONTS.display} fontSize="16px" fontWeight={600} ml={0.5} noWrap sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                            Loyalty Program
                        </Typography>
                    </Stack>
                </Box>

                {/* ── Bottom Sheet Content ── */}
                <Stack
                    sx={{
                        position: "relative", zIndex: 1, bgcolor: "white", boxSizing: "border-box",
                        px: 2, pt: 1, pb: "calc(16px + env(safe-area-inset-bottom))",
                        flex: 1,
                    }}
                    spacing={2}
                >
                    {/* Tutorial overlay */}
                    {showTutorial && (
                        <Stack sx={{ ...CARD_STYLES, p: 2, bgcolor: alpha(primary, 0.04) }} spacing={1.5}>
                            <Typography fontFamily={FONTS.display} fontWeight={700} fontSize="16px">
                                Welcome to Loyalty
                            </Typography>
                            <Typography fontSize="13px" color="text.secondary" lineHeight="18px">
                                Two engines to grow your business organically:
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                <Box sx={iconBoxSx}><PeopleRounded sx={{ fontSize: 18, color: primary }} /></Box>
                                <Stack>
                                    <Typography fontFamily={FONTS.display} fontWeight={600} fontSize="13px">Referral & First-Visit</Typography>
                                    <Typography fontSize="12px" color="text.secondary">New clients get a discount; existing clients earn by referring friends.</Typography>
                                </Stack>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                <Box sx={iconBoxSx}><StarRounded sx={{ fontSize: 18, color: primary }} /></Box>
                                <Stack>
                                    <Typography fontFamily={FONTS.display} fontWeight={600} fontSize="13px">Client Subscription</Typography>
                                    <Typography fontSize="12px" color="text.secondary">Clients pay monthly for premium perks like discounts and cashback.</Typography>
                                </Stack>
                            </Stack>
                            <ButtonBase onClick={dismissTutorial} sx={{ alignSelf: "flex-end", px: 2, py: 0.75, borderRadius: "10px", bgcolor: primary }}>
                                <Typography fontFamily={FONTS.display} fontWeight={600} fontSize="13px" color="white">Got it</Typography>
                            </ButtonBase>
                        </Stack>
                    )}

                    {/* Engine 1 */}
                    <Stack sx={CARD_STYLES} spacing={1.5} p={2}>
                        <Stack direction="row" alignItems="flex-start" spacing={1}>
                            <Box sx={iconBoxSx}><PeopleRounded sx={{ fontSize: 18, color: primary }} /></Box>
                            <Stack spacing={0.25} flex={1} sx={{ minWidth: 0 }}>
                                <Typography fontFamily={FONTS.display} fontWeight={600} fontSize="15px">
                                    Referral & First-Visit
                                </Typography>
                                <Typography fontSize="12px" color="text.secondary" lineHeight={1.35}>
                                    Reward clients who refer friends, and give first-time visitors a welcome discount.
                                </Typography>
                            </Stack>
                            <ToggleButton value={referralEnabled} onClick={() => setReferralEnabled(!referralEnabled)} />
                        </Stack>
                        {referralEnabled && (
                            <Stack spacing={1.5} pt={1}>
                                <ConfigRow label="Referral discount %" hint="Discount a new client gets when referred" value={referralDiscount} onChange={setReferralDiscount} min={0} max={100} />
                                <ConfigRow label="Referral reward %" hint="Cashback the referrer earns from their friend's payment" value={referralReward} onChange={setReferralReward} min={0} max={100} />
                                <ConfigRow label="Balance expiry (days)" hint="Days before unused balance expires" value={balanceExpiryDays} onChange={setBalanceExpiryDays} min={1} max={365} />
                                <ConfigRow label="Max balance payment %" hint="Max % of service price payable from balance" value={maxBalancePct} onChange={setMaxBalancePct} min={0} max={100} />
                            </Stack>
                        )}
                    </Stack>

                    {/* Engine 2 */}
                    <Stack sx={CARD_STYLES} spacing={1.5} p={2}>
                        <Stack direction="row" alignItems="flex-start" spacing={1}>
                            <Box sx={iconBoxSx}><StarRounded sx={{ fontSize: 18, color: primary }} /></Box>
                            <Stack spacing={0.25} flex={1} sx={{ minWidth: 0 }}>
                                <Typography fontFamily={FONTS.display} fontWeight={600} fontSize="15px">
                                    Client Subscription
                                </Typography>
                                <Typography fontSize="12px" color="text.secondary" lineHeight={1.35}>
                                    Let clients pay monthly for premium perks like discounts, cashback, and priority booking.
                                </Typography>
                            </Stack>
                            <ToggleButton value={subscriptionEnabled} onClick={() => setSubscriptionEnabled(!subscriptionEnabled)} />
                        </Stack>
                        {subscriptionEnabled && (
                            <Stack spacing={1.5} pt={1}>
                                <ConfigRow label="Subscription price" hint="Monthly price for clients" value={subPrice} onChange={setSubPrice} min={0} max={999999} />
                                <NullableConfigRow label="Booking discount %" hint="Discount on every booking for subscribers" value={subDiscount} onChange={setSubDiscount} min={0} max={100} />
                                <NullableConfigRow label="Cashback %" hint="Balance credited after each completed visit" value={subCashback} onChange={setSubCashback} min={0} max={100} />
                                <NullableToggleRow label="Balance never expires" hint="Subscriber balance stays forever" value={subNeverExpire} onChange={setSubNeverExpire} />
                                <NullableConfigRow label="Min booking notice (hours)" hint="How far in advance subscribers can book" value={subShortNotice} onChange={setSubShortNotice} min={0} max={168} />
                                <NullableConfigRow label="Min cancel window (hours)" hint="Latest a subscriber can cancel without penalty" value={subShortCancel} onChange={setSubShortCancel} min={0} max={168} />
                                <NullableToggleRow label="Waitlist priority" hint="Subscribers jump to the front of the waitlist" value={subWaitlistPriority} onChange={setSubWaitlistPriority} />
                            </Stack>
                        )}
                    </Stack>

                    {/* App booking discount */}
                    <Stack sx={CARD_STYLES} spacing={1.5} p={2}>
                        <Stack direction="row" alignItems="flex-start" spacing={1}>
                            <Box sx={iconBoxSx}><PhoneIphoneRounded sx={{ fontSize: 18, color: primary }} /></Box>
                            <Stack spacing={0.25} flex={1} sx={{ minWidth: 0 }}>
                                <Typography fontFamily={FONTS.display} fontWeight={600} fontSize="15px">
                                    App booking discount
                                </Typography>
                                <Typography fontSize="12px" color="text.secondary" lineHeight={1.35}>
                                    Clients who book from the installed app get an automatic discount on every booking. Encourages app installs and keeps clients connected via push notifications.
                                </Typography>
                            </Stack>
                            <ToggleButton value={appDiscountEnabled} onClick={() => setAppDiscountEnabled(!appDiscountEnabled)} />
                        </Stack>
                        {appDiscountEnabled && (
                            <Stack spacing={1.5} pt={1}>
                                <ConfigRow label="App discount %" hint="Discount applied automatically when booking from the installed app" value={appDiscountPct} onChange={setAppDiscountPct} min={0} max={100} />
                            </Stack>
                        )}
                    </Stack>

                    {/* Flex spacer — pushes Save button toward the bottom so short
                        content doesn't leave a large white gap above the CTA. */}
                    <Box flex={1} minHeight={16} />

                    {/* Save — inline in the scroll flow, not sticky, to avoid the
                        floating-button gap when only two toggles are shown. */}
                    <Stack alignItems="center" sx={{ pt: 1 }}>
                        <BottomButton onClick={onSave} disabled={saving} width="100%">
                            {saving ? "Saving..." : "Save"}
                        </BottomButton>
                    </Stack>
                </Stack>
            </Stack>
        </>
    );
};

const ConfigRow = ({ label, hint, value, onChange, minValue }: {
    label: string; hint?: string; value: number; onChange: (v: number) => void; min: number; max: number; minValue?: number;
}) => (
    <Stack spacing={0.25}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack flex={1} sx={{ minWidth: 0 }}>
                <Typography fontSize="13px" fontWeight={500}>{label}</Typography>
                {hint && <Typography fontSize="11px" color="text.secondary" lineHeight={1.3}>{hint}</Typography>}
            </Stack>
            <NumberInput value={value} onChange={onChange} minValue={minValue} width={80} />
        </Stack>
    </Stack>
);

const NullableConfigRow = ({ label, hint, value, onChange }: {
    label: string; hint?: string; value: number | null; onChange: (v: number | null) => void; min: number; max: number;
}) => {
    const enabled = value !== null;
    return (
        <Stack spacing={0.25}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack flex={1} sx={{ minWidth: 0 }}>
                    <Typography fontSize="13px" fontWeight={500}>{label}</Typography>
                    {hint && <Typography fontSize="11px" color="text.secondary" lineHeight={1.3}>{hint}</Typography>}
                </Stack>
                <ToggleButton value={enabled} onClick={() => onChange(enabled ? null : 0)} />
            </Stack>
            <Stack
                direction="row"
                justifyContent="flex-end"
                sx={{
                    maxHeight: enabled ? 64 : 0,
                    opacity: enabled ? 1 : 0,
                    overflow: "hidden",
                    transition: "max-height 0.15s ease, opacity 0.12s ease",
                }}
            >
                <NumberInput value={value ?? 0} onChange={(v) => onChange(v)} width={80} />
            </Stack>
        </Stack>
    );
};

const NullableToggleRow = ({ label, hint, value, onChange }: {
    label: string; hint?: string; value: boolean | null; onChange: (v: boolean | null) => void;
}) => (
    <Stack spacing={0.25}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack flex={1} sx={{ minWidth: 0 }}>
                <Typography fontSize="13px" fontWeight={500}>{label}</Typography>
                {hint && <Typography fontSize="11px" color="text.secondary" lineHeight={1.3}>{hint}</Typography>}
            </Stack>
            <ToggleButton value={value === true} onClick={() => onChange(value === true ? null : true)} />
        </Stack>
    </Stack>
);

export default LoyaltyConfig;
